import { Box, Tooltip } from "@mui/material"
import WarningIcon from "@mui/icons-material/WarningRounded"
import CheckIcon from "@mui/icons-material/Check"
import { DateTime } from "luxon"

import type { TFunction } from "i18next"
import type { GridColDef, GridSortDirection } from "@mui/x-data-grid"

import type {
  BatteryStatus as BatteryStatusType,
  DevicesTableRow,
  DeviceReportingStatus,
  DeviceRowInfo,
  InstalledOnInfo,
  InstalledOnLoading,
  InstalledOnColumnContent,
} from "types/device.types"
import type { DeviceGroupRef, DeviceTableAlertStatus } from "types/group.types"
import type { AlertSetting } from "types/alerts.types"

import { formatDateTime } from "helpers/utils/dateUtils"
import { hasDeviceTriggeredAlerts } from "helpers/utils/alerts"
import { getGroupsString, renderDashIfEmpty } from "helpers/utils/tables"
import { compare } from "helpers/utils/common"
import { nameOrUnnamed } from "helpers/utils/translations"
import GroupChips from "widgets/deviceGroups/GroupChips"
import BatteryStatus from "widgets/device/DevicesTable/BatteryStatus"
import InstalledOnCell from "widgets/device/DevicesTable/InstalledOnCell"
import { getSpanDisplayName, getSpanIndex } from "helpers/utils/spans"
import ReportingStatus from "widgets/device/ReportingStatus"
import SkeletonBox from "widgets/common/SkeletonBox"
import EntityCell from "widgets/common/EntityCell"
import {
  getBatteryStatus,
  getDeviceReportingStatus,
  getLastMessageDateTime,
} from "./devices"

// mui-datatables' sortCompare took the direction and was used as is; getSortComparator is the
// same contract, so the old comparators port unchanged.
const byDirection =
  <V,>(compare: (a: V, b: V, order: "asc" | "desc") => number) =>
  (direction: GridSortDirection) =>
    direction ? (a: V, b: V) => compare(a, b, direction) : undefined

const searchIncludes = (text: string | undefined | null, search: string) =>
  (text ?? "").toLowerCase().includes(String(search).toLowerCase())

const reportingStatusOrder: Record<DeviceReportingStatus, number> = {
  reporting: 0,
  active: 1,
  intermittent: 2,
  inactive: 3,
  unknown: 4,
}

// The asset half of an installed on reference, as text. A span's own name is
// fetched per rendered cell and never reaches the row, so it reads by its
// position on the line.
const getInstalledOnParentName = (
  { type, spanId, tower, powerline }: InstalledOnInfo,
  t: TFunction,
) =>
  type === "tower"
    ? tower?.name
    : spanId
      ? getSpanDisplayName({ id: spanId, name: null }, powerline?.span_ids, t)
      : undefined

export const getDeviceTableColumns = (
  baseOrgURL: string,
  t: TFunction,
): GridColDef<DevicesTableRow>[] => {
  const translateReportingStatus = (status: DeviceReportingStatus) =>
    t(`reporting_status.${status.toUpperCase()}`)
  const installedOnTypeLabel = ({ installedOn }: InstalledOnColumnContent) =>
    installedOn?.type ? t(`entities.${installedOn.type.toUpperCase()}`) : ""

  return [
    {
      field: "alertStatus",
      headerName: t("device_table.ALERT_STATUS"),
      width: 76,
      cellClassName: "cell--alertStatus",
      headerClassName: "cell--alertStatus",
      renderHeader: () => <WarningIcon aria-label="alert status" fontSize="small" />,
      valueFormatter: (value: DeviceTableAlertStatus) =>
        value.status ? t("alerts.TRIGGERED") : t("alerts.NOT_TRIGGERED"),
      getApplyQuickFilterFn: () => null,
      getSortComparator: byDirection<DeviceTableAlertStatus>((a, b, order) => {
        const by =
          order === "asc"
            ? (o: DeviceTableAlertStatus) => [!o.status, o.lastDataReceived]
            : (o: DeviceTableAlertStatus) => [o.status, o.lastDataReceived]
        return compare(by(b), by(a))
      }),
      renderCell: ({ value }) => (
        <Box width={"fit-content"}>
          {value.status ? (
            <Tooltip title={t("alerts.TRIGGERED_ALERT")}>
              <WarningIcon color="error" fontSize="small" />
            </Tooltip>
          ) : (
            <Tooltip title={t("alerts.NO_TRIGGERED_ALERTS")}>
              <CheckIcon fontSize="small" color="success" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "device",
      headerName: t("device_table.DEVICE"),
      flex: 1,
      minWidth: 180,
      getSortComparator: byDirection<{ name: string }>((a, b, order) => {
        const result = (a.name || "").localeCompare(b.name || "")
        return order === "desc" ? -result : result
      }),
      getApplyQuickFilterFn: (search) => (value: { name: string; id: number }) =>
        searchIncludes(nameOrUnnamed(value.name, t), search) ||
        searchIncludes(String(value.id), search),
      renderCell: ({ value }) => (
        <EntityCell
          entity="device"
          id={value.id}
          name={value.name}
          baseOrgURL={baseOrgURL}
        />
      ),
    },
    // Exported, never shown: the device column shows both in one cell.
    {
      field: "deviceNameCsv",
      headerName: t("device_table.NAME"),
      valueFormatter: (value: string | undefined) => value ?? "",
    },
    {
      field: "deviceIdCsv",
      headerName: t("device_table.ID"),
      valueFormatter: (value: number | undefined) => value ?? "",
    },
    {
      field: "deviceVersion",
      headerName: t("device_table.VERSION"),
      flex: 1,
      minWidth: 100,
      renderCell: renderDashIfEmpty,
    },
    {
      field: "installedOn",
      headerName: t("device_table.INSTALLED_ON"),
      flex: 1,
      minWidth: 150,
      valueFormatter: (value: InstalledOnColumnContent) =>
        value.installedOn?.type ? getInstalledOnCSV(value, t) : "",
      getSortComparator: byDirection<InstalledOnColumnContent>(sortInstalledOn),
      // Both lines of the cell match.
      getApplyQuickFilterFn: (search) => (value: InstalledOnColumnContent) =>
        !!value.installedOn &&
        (searchIncludes(installedOnTypeLabel(value), search) ||
          searchIncludes(
            [
              getInstalledOnParentName(value.installedOn, t),
              value.installedOn.powerline?.name,
            ]
              .filter(Boolean)
              .join(" "),
            search,
          )),
      renderCell: ({ value }) =>
        value.installedOn ? (
          <InstalledOnCell installedOn={value.installedOn} loading={value.loading} />
        ) : (
          "-"
        ),
    },
    {
      field: "groups",
      headerName: t("device_table.GROUPS"),
      flex: 1,
      minWidth: 200,
      valueFormatter: (groups: DeviceGroupRef[]) =>
        (groups ?? []).map((group) => group.name).join(" - "),
      getSortComparator: byDirection<DeviceGroupRef[]>((a, b, order) =>
        order === "asc"
          ? getGroupsString(a).localeCompare(getGroupsString(b))
          : getGroupsString(b).localeCompare(getGroupsString(a)),
      ),
      getApplyQuickFilterFn: (search) => (groups: DeviceGroupRef[]) =>
        (groups ?? []).some((group) => searchIncludes(group.name, search)),
      renderCell: ({ value }) => (value ? <GroupChips groups={value} noWrap /> : null),
    },
    {
      field: "lastDataReceived",
      headerName: t("device_table.LAST_DATA_RECEIVED"),
      flex: 1,
      minWidth: 130,
      valueFormatter: (value: { value: string }) =>
        value.value === "-" ? "" : value.value,
      getSortComparator: byDirection<{ value: string }>((a, b, order) =>
        order === "asc" ? a.value.localeCompare(b.value) : b.value.localeCompare(a.value),
      ),
      getApplyQuickFilterFn: (search) => (value: { value: string }) =>
        value.value.includes(String(search)),
      renderCell: (params) =>
        params.value.statusesLoading ? (
          <SkeletonBox height={"20px"} width={"120px"} />
        ) : (
          renderDashIfEmpty(params)
        ),
    },
    {
      field: "reportingStatus",
      headerName: t("device_table.STATUS"),
      flex: 1,
      minWidth: 120,
      cellClassName: "cell--reportingStatus",
      headerClassName: "cell--reportingStatus",
      valueFormatter: (value: { value: DeviceReportingStatus }) =>
        translateReportingStatus(value.value),
      getSortComparator: byDirection<{ value: DeviceReportingStatus }>((a, b, order) => {
        const value1 = reportingStatusOrder[a.value]
        const value2 = reportingStatusOrder[b.value]
        return order === "asc" ? value1 - value2 : value2 - value1
      }),
      getApplyQuickFilterFn: (search) => (value: { value: DeviceReportingStatus }) =>
        searchIncludes(translateReportingStatus(value.value), search),
      renderCell: ({ value }) =>
        value.statusesLoading ? (
          <SkeletonBox height={"20px"} width={"100px"} />
        ) : (
          <ReportingStatus status={value.value} />
        ),
    },
    {
      field: "batteryStatus",
      headerName: t("device_table.BATTERY"),
      flex: 1,
      minWidth: 100,
      valueFormatter: (value: DevicesTableRow["batteryStatus"]) =>
        value?.value ? `${value.value}%` : "",
      getApplyQuickFilterFn: () => null,
      getSortComparator: byDirection<DevicesTableRow["batteryStatus"]>((a, b, order) => {
        // Treat a missing level as lower than 0
        const value1 = a?.value ?? -Infinity
        const value2 = b?.value ?? -Infinity
        return order === "asc" ? value1 - value2 : value2 - value1
      }),
      renderCell: ({ row: { batteryStatus } }) => (
        <BatteryStatus
          value={batteryStatus.value as number}
          status={batteryStatus.status as BatteryStatusType | null}
          timestamp={batteryStatus.timestamp as string}
          isLoading={batteryStatus.isLoading}
        />
      ),
    },
  ]
}

// The CSV keeps the old column order; the device column itself is not exported.
export const DEVICES_TABLE_CSV_FIELDS = [
  "alertStatus",
  "deviceNameCsv",
  "deviceIdCsv",
  "deviceVersion",
  "installedOn",
  "groups",
  "lastDataReceived",
  "reportingStatus",
  "batteryStatus",
]

export const getDevicesTableRows = (
  devices: DeviceRowInfo[],
  orgAlertsBySettingId: { [key: string]: AlertSetting },
  statusesLoading: boolean,
  installedOnLoading?: InstalledOnLoading,
): DevicesTableRow[] => {
  const now = DateTime.now()
  return devices?.map((deviceWExtras) => {
    const device = deviceWExtras.device
    const groups = deviceWExtras.groups
    const status = deviceWExtras.status
    const installedOn = deviceWExtras.installedOn
    const lastMessageDateTime = getLastMessageDateTime(status)
    return {
      id: device.less_id,
      alertStatus: deviceWExtras.settingsStates
        ? {
            status: hasDeviceTriggeredAlerts(
              deviceWExtras.settingsStates,
              orgAlertsBySettingId,
            ),
            lastDataReceived: lastMessageDateTime,
          }
        : { status: false, lastDataReceived: lastMessageDateTime },
      device: { name: device.name, id: device.less_id },
      deviceNameCsv: device.name,
      deviceIdCsv: device.less_id,
      deviceVersion: device.settings?.device_version ?? "",
      installedOn: { installedOn, loading: installedOnLoading },
      groups,
      lastDataReceived: {
        value: formatDateTime(lastMessageDateTime),
        statusesLoading,
      },
      reportingStatus: {
        value: getDeviceReportingStatus(status, now),
        statusesLoading,
      },
      batteryStatus: getBatteryStatus(deviceWExtras.status, statusesLoading),
    }
  })
}

export const sortInstalledOn = (
  a: InstalledOnColumnContent,
  b: InstalledOnColumnContent,
  order: "asc" | "desc",
) => {
  const getName = (item: InstalledOnColumnContent): string | null | undefined => {
    const info = item.installedOn
    switch (info?.type) {
      case "tower":
        return info.tower?.name
      case "span": {
        // By line, then by position on it: the name is fetched per cell and the
        // sort never sees it. The id keeps two lines with one name apart.
        const index = getSpanIndex(Number(info.spanId), info.powerline?.span_ids)
        if (index === null) return undefined

        const pad = (value: number) => value.toString().padStart(6, "0")
        return `${info.powerline?.name ?? ""} ${pad(info.powerline?.id ?? 0)} ${pad(index)}`
      }
      default:
        return undefined
    }
  }

  const getTypePriority = (item: InstalledOnColumnContent): number => {
    switch (item.installedOn?.type) {
      case "span":
        return 1
      case "tower":
        return 2
      default:
        return 3
    }
  }

  const loadingA =
    a.installedOn?.type === "tower" ? a.loading?.towers : a.loading?.powerlines
  const loadingB =
    b.installedOn?.type === "tower" ? b.loading?.towers : b.loading?.powerlines

  if (loadingA && !loadingB) return 1
  if (!loadingA && loadingB) return -1

  const aPriority = getTypePriority(a)
  const bPriority = getTypePriority(b)

  if (aPriority !== bPriority) {
    return order === "asc" ? aPriority - bPriority : bPriority - aPriority
  }

  const aName = getName(a)
  const bName = getName(b)

  if (!aName && !bName) return 0

  if (!aName) {
    return order === "asc" ? 1 : -1
  }
  if (!bName) {
    return order === "asc" ? -1 : 1
  }

  const nameComparison = aName.localeCompare(bName)
  return order === "asc" ? nameComparison : -nameComparison
}

// The same two levels the cell shows, flattened: the asset the device is on,
// then the line it belongs to.
export const getInstalledOnCSV = (field: InstalledOnColumnContent, t: TFunction) => {
  const installedOn = field.installedOn
  if (!installedOn?.type) return "-"

  const parentName = getInstalledOnParentName(installedOn, t)

  const label = t(`entities.${installedOn.type.toUpperCase()}`)
  // A span's name already starts with its type ("Span 42"), a tower's does not.
  const parent =
    parentName && installedOn.type === "tower" ? `${label}: ${parentName}` : parentName

  return [parent || label, installedOn.powerline?.name].filter(Boolean).join(" - ")
}
